import { forwardRef, useState, useRef, useImperativeHandle, useEffect } from 'react';
import styled from 'styled-components';

import { ReactComponent as LeftArrowIcon } from '../../image/LeftArrow.svg';
import { useLanguage } from '../../Context/LanguageContext';
import { useTheme } from '../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../Styles/theme';

const LockerSelectBox = forwardRef((props, ref) => {
  const { options, disabled, changeFn } = props;
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

	const [isClicked, setIsClicked] = useState(false);
	const [defaultVal, setDefaultVal] = useState({name: t('memberDetail.lockerModal.lockerSelectDefault')});

	const TopLevelRef = useRef();
	const SelectRef = useRef();
	const isClosingRef = useRef(false);

	useImperativeHandle(ref, () => ({
		getValue: (val) => {
			return defaultVal.value;
		},
		setDefaultVal: (categoryData, memberShipData, val) => {
			for (var i = 0; i < memberShipData.length; i++) {
				if (memberShipData[i].name === val) {
					var categoryId = memberShipData[i].categoryId;

					for (var j = 0; j < categoryData.length; j++) {
						if (categoryData[j].id === categoryId) {
							setDefaultVal(categoryData[j]);
							return;
						}
					}
					return;
				}
			}
		},
		setValue: (val) => {
			setDefaultVal(val);
		},
		setDefaultValForId: (val) => {
			var lessonData = undefined;

			for (var i = 0; i < options.length; i++) {
				if (options[i].id === val) {
					lessonData = options[i];
					break;
				}
			}

			if (lessonData) {
				setDefaultVal(lessonData);
				return 'Success';
			}
			else {
				alert('존재하지 않는 수업입니다.');
				return 'Fail';
			}
		}
	}))

  // 컴포넌트 외부 클릭시 이벤트를 받기위한 리스너 연결
  useEffect(() => {
    document.addEventListener("mousedown", onClickOutSide);

    return () => {
      document.removeEventListener("mousedown", onClickOutSide);
    }
  }, [])

  function closeSelectBox() {
    isClosingRef.current = true;
    SelectRef.current.style.transition = 'height 0.3s ease-in-out';
    SelectRef.current.style.height = '0px';

    SelectRef.current.addEventListener('transitionend', function handler() {
      if (!isClosingRef.current) return;
      SelectRef.current.style.border = 'none';
      SelectRef.current.style.visibility = 'hidden';
      isClosingRef.current = false;
      setIsClicked(false);
    }, { once: true });
  }

  // 컴포넌트 외부 클릭시 실행
  function onClickOutSide(e) {
    if (TopLevelRef.current !== e.target && !TopLevelRef.current.contains(e.target)) {
      closeSelectBox();
    }
  }

	function onClick() {
    if (disabled) return;
		else if (!options[0]) return;

		if (isClicked) {
      closeSelectBox();
		}
		else {
			isClosingRef.current = false;
			SelectRef.current.style.transition = 'height 0.3s ease-in-out';
			SelectRef.current.style.border = `1px solid ${theme.primary}`;
			SelectRef.current.style.height = `${32 * (options.length)}px`;
			SelectRef.current.style.maxHeight = '130px';
			SelectRef.current.style.overflowY = 'scroll';
			SelectRef.current.style.overflowX = 'none';
			SelectRef.current.style.visibility = 'visible';
			setIsClicked(true);
		}
	}

	function optionalClick(e, index) {
		e.stopPropagation();
		setDefaultVal(options[index]);
		changeFn(options[index]);
    closeSelectBox();
	}

	return (
		<TopLevelWrapper ref={TopLevelRef}>
			<ViewBox
				isClicked={isClicked}
				disabled={disabled}
				onClick={onClick}>
				<ViewText $disabled={disabled}>{defaultVal.name}</ViewText>
				<ArrowIcon $isClicked={isClicked} $disabled={disabled} />
			</ViewBox>
			<SelectBox
				ref={SelectRef}
				onClick={onClick}>
				{
					options.map((data, index) =>
						<OptionWrapper key={index} onClick={(e) => optionalClick(e, index)}>
							<Option>{data.name}</Option>
						</OptionWrapper>
					)
				}
			</SelectBox>
		</TopLevelWrapper>
	)
})

export default LockerSelectBox;

const TopLevelWrapper = styled.div`
	display: flex;
	flex-direction: column;

	width: 130px;
	height: 35px;
`;

const ViewBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  padding-right: 5px;

  width: 100%;
  box-sizing: border-box;
  height: 35px;

  border: 1px solid ${props => props.disabled ? props.theme.buttonFill : props.theme.line};
  border-radius: 5px;
  overflow: hidden;

  background-color: ${props => props.disabled ? props.theme.buttonFill : props.theme.subBackground};

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
    }
  }

  transition: all 0.3s ease-in-out;
`;

const ViewText = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.theme.text};
  margin: 0 0 0 8px;

  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ArrowIcon = styled(LeftArrowIcon)`
  width: 8px;
  height: auto;
  margin-right: 6px;
  flex-shrink: 0;
  display: ${props => props.$disabled ? 'none' : 'block'};
  transform: ${props => props.$isClicked ? 'rotate(90deg)' : 'rotate(-90deg)'};
  transition: transform 0.3s ease-in-out;

  & path {
    fill: ${props => props.theme.subText};
  }
`;

const SelectBox = styled.div`
	position: absolute;
	display: flex;
	flex-direction: column;

	justify-content: flex-start;
	align-items: flex-start;

	width: 181px;
	height: 0px;

  margin-top: 38px;

	border-radius: 5px;
	overflow: hidden;
	visibility: hidden;

	background-color: ${props => props.theme.subBackground};

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
    }
  }

	&::-webkit-scrollbar {
		width: 9px;
	}

	&::-webkit-scrollbar-thumb {
    box-shadow: inset 0 0 10px 20px ${props => props.theme.primary};
		border: 3px solid transparent;
		height: 25px;
		border-radius: 200px;
	}

	&::-webkit-scrollbar-track {
    box-shadow: inset 0 0 10px 10px transparent;
		border: 4px solid transparent;
		background-color: transparent;
	}

	&::-webkit-scrollbar-track-piece {
		background-color: transparent;
	}
`;

const OptionWrapper = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const Option = styled.p`
	font-family: NotoSansKR-Regular;
	font-size: 14px;
	color: ${props => props.theme.text};
	margin: 6px 0 6px 8px;
`;
